import "@/assets/index.css";
import type { LDFlagSet } from "launchdarkly-react-client-sdk";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";

import StorageError from "@/components/StorageError";
import { config } from "@/config.ts";
import { storageInitError } from "@/db/db.ts";
import { installPGliteFsRejectionSuppression } from "@/db/suppressFsRejection.ts";
import { initializeGoogleAnalytics } from "@/init/googleAnalytics.ts";
import { initializeHoneycomb } from "@/init/honeycomb.ts";
import { createLDClient, initializeLaunchDarkly } from "@/init/launchDarkly.tsx";
import { initializeLogLevel } from "@/init/loglevel.ts";
import { initializePreloadErrorHandler } from "@/init/preloadError.ts";
import { initializeSentry } from "@/init/sentry.ts";
import { initializeTimings } from "@/init/timings.ts";
import { initializeLogChannels } from "@/logger";
import { router } from "@/routes";
import { isQuotaExceededError, localStorageCheck } from "@/utils/localStorage.ts";
import { migrate } from "./db/migrate";
import { runStorageProbe } from "./init/storageProbe";

const isDebug = config.debugMode;
localStorageCheck();

window.addEventListener("unhandledrejection", (event) => {
  const error = event.reason;
  if (isQuotaExceededError(error)) {
    event.preventDefault();
    console.warn("[STORAGE-QUOTA] Caught unhandled QuotaExceededError in promise rejection, running cleanup");
    localStorageCheck();
  }
});

installPGliteFsRejectionSuppression(window, console, "PGLITE");

const ldClient = createLDClient(import.meta.env.VITE_LD_CLIENT_SIDE_ID);
const activeLdClient = initializeLaunchDarkly(ldClient, import.meta.env.VITE_LD_CLIENT_SIDE_ID);
initializeLogLevel({ logLevel: import.meta.env.VITE_LOG_LEVEL });
initializeLogChannels();

initializeSentry({
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  dsn: "https://f8871862445f42c0934637c048b69182@o1146727.ingest.sentry.io/4505133231702016",
  mode: import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE,
});

initializeGoogleAnalytics({ key: import.meta.env.VITE_GA_KEY });

initializePreloadErrorHandler();

initializeTimings();

// Measure IndexedDB write latency and report to Sentry for storage I/O visibility
runStorageProbe().then(() => {});

if (storageInitError) {
  // Storage is unusable — render the recovery UI and stop bootstrapping. The
  // failure was already reported to Sentry as a handled warning by
  // reportStorageUnavailable(); throwing here would surface as an unhandled
  // window.onerror event and double-report it (see WEB-1982).
  ReactDOM.createRoot(document.getElementById("root")!).render(<StorageError reason={storageInitError.reason} />);
} else {
  const [LDProvider] = await Promise.all([activeLdClient, migrate()]);

  const allFlags: LDFlagSet = ldClient?.allFlags();
  const inActiveTelemetry = allFlags?.inActiveTelemetry ?? false;
  initializeHoneycomb({
    ingestApiKey: import.meta.env.VITE_HONEYCOMB_INGEST_API_KEY,
    serviceName: `web-${import.meta.env.VITE_HONEYCOMB_ENVIRONMENT}`,
    isDebug,
    active: !inActiveTelemetry,
  });

  const routerWrapper = LDProvider ? (
    <LDProvider>
      <RouterProvider router={router} />
    </LDProvider>
  ) : (
    <>
      <RouterProvider router={router} />
    </>
  );

  ReactDOM.createRoot(document.getElementById("root")!).render(routerWrapper);
}
